/**
 * SelectConnectorType - Step 1 of Add Connector Wizard
 *
 * Compact single-column list of available connector types with real platform
 * brand glyphs, search, and category filters. Rows are selectable controls.
 */
import React, { useState, useMemo } from 'react';
import { Database, Search, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useConnectorTypes } from '@/hooks/useConnectorTypes';
import { PlatformLogo } from '@/components/connectors/library/PlatformLogo';
import type { ConnectorType, ConnectorTypeInfo } from '@/types/connectors';

interface SelectConnectorTypeProps {
  selectedType: ConnectorType | null;
  onSelectType: (type: ConnectorType) => void;
}

/**
 * Loading skeleton: a vertical stack of row-shaped placeholders.
 */
const ConnectorTypeListSkeleton: React.FC = () => {
  return (
    <div className="space-y-6" data-testid="connector-types-loading">
      <Skeleton className="h-10 w-full" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-7 w-20" />
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-7 w-28" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border px-4 py-3">
            <Skeleton className="h-7 w-7 rounded" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="ml-auto h-3 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * A single selectable connector-type row.
 */
const ConnectorTypeRow: React.FC<{
  option: ConnectorTypeInfo;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ option, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      data-testid="connector-type-row"
      data-connector-type={option.connector_type}
      data-connector-name={option.display_name}
      aria-pressed={isSelected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border bg-card px-4 py-3 text-left transition-colors',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected && 'border-primary bg-primary/5 ring-2 ring-primary/20'
      )}
    >
      <PlatformLogo connectorType={option.connector_type} size={28} />
      <span className="text-sm font-medium">{option.display_name}</span>
      <span className="ml-auto text-xs capitalize text-muted-foreground">{option.category}</span>
      {option.popular && (
        <Badge variant="secondary" className="text-xs">
          Popular
        </Badge>
      )}
      <ChevronRight
        className={cn('h-4 w-4 shrink-0 text-muted-foreground', isSelected && 'text-primary')}
      />
    </button>
  );
};

/**
 * Main SelectConnectorType Component
 */
export const SelectConnectorType: React.FC<SelectConnectorTypeProps> = ({
  selectedType,
  onSelectType,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Only available connectors are offered for creation.
  const { data, isLoading, error, refetch } = useConnectorTypes({ available_only: true });

  // Available connectors, filtered by search + category, sorted alphabetically.
  const filteredTypes = useMemo(() => {
    const connectorTypes = data?.connector_types;
    if (!connectorTypes) return [];

    let filtered = connectorTypes.filter((type) => type.available);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (type) =>
          type.display_name.toLowerCase().includes(query) ||
          type.description.toLowerCase().includes(query) ||
          type.category.toLowerCase().includes(query)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((type) => type.category === selectedCategory);
    }

    return [...filtered].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [data?.connector_types, searchQuery, selectedCategory]);

  // Category filter chips, with counts from the API.
  const categories = useMemo(() => {
    const byCategory = data?.by_category;
    if (!byCategory) return [];

    return Object.entries(byCategory).map(([cat, count]) => ({
      value: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
      count,
    }));
  }, [data?.by_category]);

  if (isLoading) {
    return <ConnectorTypeListSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load connector types</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : 'An error occurred while fetching connector types.'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetch()} variant="outline" className="w-full">
          <Loader2 className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search connector types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2">
        <Badge
          variant={selectedCategory === null ? 'default' : 'outline'}
          className="cursor-pointer px-3 py-1 transition-colors hover:bg-primary/80"
          onClick={() => setSelectedCategory(null)}
        >
          All ({data?.total || 0})
        </Badge>
        {categories.map((category) => (
          <Badge
            key={category.value}
            variant={selectedCategory === category.value ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1 transition-colors hover:bg-primary/80"
            onClick={() =>
              setSelectedCategory(selectedCategory === category.value ? null : category.value)
            }
          >
            {category.label} ({category.count})
          </Badge>
        ))}
      </div>

      {/* Connector type list */}
      {filteredTypes.length > 0 ? (
        <div className="space-y-2">
          {filteredTypes.map((option) => (
            <ConnectorTypeRow
              key={option.connector_type}
              option={option}
              isSelected={selectedType === option.connector_type}
              onSelect={() => onSelectType(option.connector_type)}
            />
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <Database className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">No connectors match your search</p>
        </div>
      )}
    </div>
  );
};
